import { useCallback } from 'react';
import { useHabitContext } from '../context/HabitContext';
import { useTodos } from './useTodos';
import { filterTodosForDisplay } from '../utils/recurrenceUtils';
import { toLocalDateStr } from '../utils/dateUtils';
import type { DayProgress } from '../components/DailyTracker/DatePickerPopup';
import type { Habit } from '../types';

const isHabitActiveOnDate = (habit: Habit, date: Date) => {
  if (!habit.createdAt) return true;
  const created = new Date(habit.createdAt);
  created.setHours(0, 0, 0, 0);
  const sel = new Date(date);
  sel.setHours(0, 0, 0, 0);
  return created <= sel;
};

export function useDayCompletion(selectedDate: Date) {
  const { categories, getHabitEntriesFor } = useHabitContext();
  const dateStr = toLocalDateStr(selectedDate);
  const { allTodos } = useTodos(dateStr);

  const allHabits = categories.flatMap((c) => c.habits);
  const trackerHabits = allHabits.filter((h) => isHabitActiveOnDate(h, selectedDate));

  const completedHabits = trackerHabits.filter((h) => {
    const entry = getHabitEntriesFor(h.id, selectedDate.getMonth(), selectedDate.getFullYear())
      .find((e) => e.day === selectedDate.getDate());
    if (!entry) return false;
    if (h.type === 'checkbox') return entry.completed === true;
    if (h.type === 'rating') return (entry.rating ?? 0) > 0;
    return false;
  }).length;

  const dayTodos = filterTodosForDisplay(allTodos, dateStr);
  const completedItems = completedHabits + dayTodos.filter((td) => td.completed).length;
  const totalItems = trackerHabits.length + dayTodos.length;

  const getDayProgress = useCallback((day: Date): DayProgress => {
    const month = day.getMonth();
    const year = day.getFullYear();
    const dayNum = day.getDate();
    const habitsForDay = allHabits.filter(
      (h) => isHabitActiveOnDate(h, day),
    );
    let completed = 0;
    habitsForDay.forEach((habit) => {
      const entry = getHabitEntriesFor(habit.id, month, year).find((e) => e.day === dayNum);
      if (!entry) return;
      if (habit.type === 'checkbox' && entry.completed === true) completed += 1;
      if (habit.type === 'rating' && (entry.rating ?? 0) > 0) completed += 1;
    });
    const dayTodos = filterTodosForDisplay(allTodos, toLocalDateStr(day));
    completed += dayTodos.filter((td) => td.completed).length;
    return { completed, total: habitsForDay.length + dayTodos.length };
  }, [allHabits, allTodos, getHabitEntriesFor]);

  return { completedItems, totalItems, getDayProgress };
}
